import { useTranslation } from "react-i18next";
import { TasksDraggableKanbanBoard } from "./TasksDraggableKanbanBoard";
import { TASK_STATUS_KANBAN_STAGES, TASK_STATUS_CONFIG, KanbanTask } from "./types";
import { useTasksFilter } from "@/contexts/TasksFilterContext";
import { useTasksFiltering } from "@/hooks/tasks/useTasksFiltering";
import { useUpdateTask } from "@/hooks/tasks/useTasks";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/providers/AuthProvider";

interface TasksKanbanViewProps {
  tasks: KanbanTask[];
  onTaskUpdated?: () => void;
}

export const TasksKanbanView = ({ tasks, onTaskUpdated }: TasksKanbanViewProps) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const updateTaskMutation = useUpdateTask();
  
  // Get the filter context to apply filtering to tasks
  const {
    searchQuery,
    statusFilter,
    priorityFilter,
    maxDaysAgo,
    showCancelled,
    ownershipFilter
  } = useTasksFilter();
  
  // Use the filtering hook to get filtered tasks
  const { filteredTasks } = useTasksFiltering(
    tasks,
    searchQuery,
    statusFilter,
    priorityFilter,
    maxDaysAgo,
    showCancelled,
    ownershipFilter,
    user?.id || null
  );
  
  const handleTaskDrop = async (taskId: string, newStatus: string) => {
    try {
      await updateTaskMutation.mutateAsync({
        id: taskId,
        status: newStatus as 'todo' | 'inProgress' | 'done' | 'cancelled'
      });

      toast({
        title: t("common.success"),
        description: t("tasks.kanban.moved", "Task moved to {{status}}", { status: newStatus }),
      });

      if (onTaskUpdated) {
        onTaskUpdated();
      }
    } catch (error) {
      console.error('Error updating task status:', error);
      toast({
        title: t("common.error"),
        description: t("tasks.kanban.moveError", "Failed to move task"),
        variant: "destructive",
      });
    }
  };

  return (
    <TasksDraggableKanbanBoard
      stages={TASK_STATUS_KANBAN_STAGES}
      tasksByStage={TASK_STATUS_KANBAN_STAGES.reduce((acc, stage) => {
        const stageTasks = filteredTasks.filter(t => (t.status || 'todo') === stage.id);
        acc[stage.id] = stageTasks;
        return acc;
      }, {} as Record<string, KanbanTask[]>)}
      onTaskDrop={handleTaskDrop}
      statusConfig={TASK_STATUS_CONFIG}
      onTaskUpdated={onTaskUpdated}
    />
  );
};
